import dagre from 'dagre';
import { Node, Edge, Position } from 'reactflow';

export interface LayoutedNode extends Node {
  sourcePosition?: Position;
  targetPosition?: Position;
}

export type LayoutedEdge = Edge;

export const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'BT' | 'LR' | 'RL' = 'TB',
  nodeWidth = 200,
  nodeHeight = 80
): { nodes: LayoutedNode[]; edges: Edge[] } => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ 
    rankdir: direction,
    nodesep: 50,  // Horizontal spacing between nodes
    ranksep: 100, // Vertical spacing between ranks
    marginx: 20,
    marginy: 20
  });

  // Add nodes to dagre graph
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  // Add edges to dagre graph
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(dagreGraph);

  // Apply layout positions to nodes
  const layoutedNodes: LayoutedNode[] = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    
    return {
      ...node,
      targetPosition: direction === 'TB' || direction === 'BT' ? Position.Top : Position.Left,
      sourcePosition: direction === 'TB' || direction === 'BT' ? Position.Bottom : Position.Right,
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

export const getHierarchicalLayout = (
  nodes: Node[],
  edges: Edge[],
  nodeWidth = 200,
  nodeHeight = 80
): { nodes: LayoutedNode[]; edges: Edge[] } => {
  return getLayoutedElements(nodes, edges, 'TB', nodeWidth, nodeHeight);
};

export const getFlowLayout = (
  nodes: Node[],
  edges: Edge[],
  nodeWidth = 200,
  nodeHeight = 80
): { nodes: LayoutedNode[]; edges: Edge[] } => {
  return getLayoutedElements(nodes, edges, 'LR', nodeWidth, nodeHeight);
};

// Auto-detect best layout based on graph structure
export const getOptimalLayout = (
  nodes: Node[],
  edges: Edge[],
  nodeWidth = 200,
  nodeHeight = 80
): { nodes: LayoutedNode[]; edges: Edge[] } => {
  // Calculate graph metrics
  const nodeCount = nodes.length;
  const edgeCount = edges.length;
  const density = edgeCount / (nodeCount * (nodeCount - 1));
  
  // For sparse hierarchical graphs, use top-bottom
  if (density < 0.3 && hasHierarchicalStructure(nodes, edges)) {
    return getLayoutedElements(nodes, edges, 'TB', nodeWidth, nodeHeight);
  }
  
  // For dense or wide graphs, use left-right
  return getLayoutedElements(nodes, edges, 'LR', nodeWidth, nodeHeight);
};

// Helper function to detect hierarchical structure
const hasHierarchicalStructure = (nodes: Node[], edges: Edge[]): boolean => {
  // Check if most nodes have data indicating levels
  const nodesWithLevels = nodes.filter(node => 
    node.data && typeof node.data.level === 'number'
  );
  
  return nodesWithLevels.length > nodes.length * 0.7;
};

// Layout presets for different use cases
export const LayoutPresets = {
  DEPENDENCY_GRAPH: { direction: 'TB' as const, nodeWidth: 250, nodeHeight: 100 },
  GOAL_HIERARCHY: { direction: 'TB' as const, nodeWidth: 200, nodeHeight: 80 },
  WORKFLOW: { direction: 'LR' as const, nodeWidth: 180, nodeHeight: 60 },
  NETWORK: { direction: 'TB' as const, nodeWidth: 150, nodeHeight: 150 }
} as const;